import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DataService } from './data.service';
import { environment } from 'src/environments/environment';
import {
  EngageClinicEventPayload,
  EngageClinicEventType,
} from '../models/engage-event.model';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return typeof value === 'string' && UUID_REGEX.test(value.trim());
}

/**
 * Service for registering clinic events with Engage.
 * Uses engageApiBaseUrl and PATCH /api/v1/engage/events/clinic/{referralId}.
 *
 * Event types and when to register each are defined in EngageClinicEventType.
 * Call registerClinicEvent() from the appropriate places when each event occurs.
 */
@Injectable({
  providedIn: 'root',
})
export class EngageEventsService {
  private get apiBaseUrl(): string {
    const base = environment.engageApiBaseUrl ?? '';
    return base.endsWith('/') ? base : base + '/';
  }

  constructor(private readonly _dataService: DataService) {}

  /**
   * Register a clinic event for an Engage referral.
   * PATCH /api/v1/engage/events/clinic/{referralId}
   *
   * @param referralId - Engage referral id (path parameter)
   * @param payload - Event payload (eventType required; optional patientId for PHI correlation)
   * @returns Observable of the API response
   */
  registerClinicEvent(
    referralId: string,
    payload: EngageClinicEventPayload
  ): Observable<unknown> {
    if (!referralId?.trim()) {
      return new Observable((observer) => {
        observer.error(new Error('referralId is required'));
        observer.complete();
      });
    }
    const url = `${this.apiBaseUrl}api/v1/engage/events/clinic/${encodeURIComponent(referralId.trim())}`;
    const body: EngageClinicEventPayload = {
      eventType: payload.eventType,
      metadata: payload.metadata ?? {},
      ...(payload.patientId != null &&
        String(payload.patientId).trim() !== '' && { patientId: String(payload.patientId).trim() }),
      ...(payload.advisorId != null && { advisorId: payload.advisorId }),
      ...(payload.triggeredBy != null && { triggeredBy: payload.triggeredBy }),
      ...(payload.encounterId != null && { encounterId: payload.encounterId }),
      ...(payload.providerId != null && { providerId: payload.providerId }),
      ...(payload.dos != null && { dos: payload.dos }),
      ...(payload.assignedAdvisor != null && { assignedAdvisor: payload.assignedAdvisor }),
      ...(payload.careTeam != null && payload.careTeam.length > 0 && { careTeam: payload.careTeam }),
      ...(payload.chartsScheduledPhysician != null &&
        isUuid(payload.chartsScheduledPhysician) && {
          chartsScheduledPhysician: payload.chartsScheduledPhysician,
        }),
    };
    return this._dataService.patch(url, body);
  }
}

// Re-export for convenience
export { EngageClinicEventType, EngageClinicEventPayload } from '../models/engage-event.model';
